import { useEffect, useMemo, useState, type ComponentType } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  Flag,
  Link2,
  Loader2,
  Plus,
  Target,
  Trash2,
  TrendingUp,
  Wallet,
  WifiOff,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import Pagination from '@/client/components/common/Pagination';
import { TooltipProvider } from '@/components/ui/tooltip';
import { MarketingPageShell } from './components/MarketingPageShell';
import {
  MarketingFilterPanel,
  MarketingFilterSearch,
  MarketingFilterSelect,
  marketingFilterSelectClass,
  marketingMatchSummary,
} from './components/MarketingListFilters';
import { ActionIconButton } from './components/ActionIconButton';
import { GoalCampaignLinker } from './components/GoalCampaignLinker';
import { PlayLinksDialog } from './components/PlayLinksDialog';
import { EmptyState, StatusBadge } from './components/marketing-ui';
import {
  FieldHint,
  FieldLabel,
  RequiredMark,
  clearFieldError,
  fieldClass,
} from '@/client/pages/Admin/shared/form-field';
import { ChipMultiSelect } from '@/client/pages/Admin/shared/ChipMultiSelect';
import {
  useCreateGoalMutation,
  useCreatePlayMutation,
  useDeletePlayMutation,
  usePlaysOverviewQuery,
  useUpdateGoalMutation,
  useUpdatePlayMutation,
} from '@/client/api/marketing/plays';
import type {
  ApiPlayWithRollup,
  ApiScoredGoal,
} from '@/client/types/marketing/api';
import { getApiErrorMessage } from '@/client/utils/errors';
import {
  GOAL_CADENCE_LABEL,
  GOAL_CADENCES,
  GOAL_HEALTH_BADGE,
  GOAL_HEALTH_LABEL,
  GOAL_UNIT_LABEL,
  GOAL_UNITS,
  MARKETING_PLAY_TAG_LABEL,
  MARKETING_PLAY_TAGS,
  MARKETING_VERTICAL_LABEL,
  MARKETING_VERTICALS,
  PLAY_CURRENCIES,
  PLAY_CURRENCY_LABEL,
  PLAY_PILLAR_LABEL,
  PLAY_STATUS_BADGE,
  PLAY_STATUS_LABEL,
  type GoalCadence,
  type GoalHealth,
  type GoalUnit,
  type MarketingPlayTag,
  type MarketingVertical,
  type PlayCurrency,
  type PlayPillar,
  type PlayStatus,
} from '@/client/types/marketing/strategy';

/**
 * Plays — the strategy spine, on real data.
 *
 * Everything on this page comes from one call to `/plays/overview`: each play
 * with a count of what hangs off it, and every goal already carrying progress,
 * pacing and a health verdict. None of that arithmetic is repeated here. The
 * alternative — loading content, series, outreach, posts and placements into the
 * browser to count them, and scoring goals client-side — is a second
 * implementation of the pacing rule, and the copy that drifts is always the one
 * someone is reading off a screen.
 *
 * "New campaign" opens a form. The mock inserted a row called "Untitled
 * campaign"; on a real table that is how you end up with four of them.
 */

const money = (minor: number | null | undefined, currency: string) =>
  minor == null ? '—' : `${currency} ${(minor / 100).toLocaleString()}`;

const pct = (n: number) => `${Math.round(n * 100)}%`;

const inputClass =
  'h-9 rounded-md border border-gray-200 bg-white px-3 text-sm outline-none ring-blue-500 focus:ring-2';

const asArray = (value: unknown): string[] =>
  Array.isArray(value) ? (value as unknown[]).map(String) : [];

/** Campaigns a goal hangs off — prefers play_ids, falls back to legacy play_id. */
const goalPlayIds = (goal: { play_id: number | null; play_ids?: number[] | null }): number[] => {
  if (Array.isArray(goal.play_ids) && goal.play_ids.length > 0) {
    const seen = new Set<number>();
    const out: number[] = [];
    for (const raw of goal.play_ids) {
      const n = Number(raw);
      if (!Number.isFinite(n) || seen.has(n)) continue;
      seen.add(n);
      out.push(n);
    }
    return out;
  }
  return goal.play_id != null ? [goal.play_id] : [];
};

type CampaignSort = 'name' | 'status' | 'start' | 'end' | 'spend' | 'updated';
type GoalSort = 'metric' | 'campaign' | 'health' | 'progress' | 'cadence';

const STATUS_RANK: Record<string, number> = {
  active: 0,
  draft: 1,
  paused: 2,
  complete: 3,
  archived: 4,
};

const HEALTH_RANK: Record<GoalHealth, number> = {
  at_risk: 0,
  behind: 1,
  on_track: 2,
  ahead: 3,
};

const HEALTH_BAR: Record<GoalHealth, string> = {
  ahead: 'bg-emerald-500',
  on_track: 'bg-blue-500',
  behind: 'bg-amber-500',
  at_risk: 'bg-rose-500',
};

const EMPTY_HEALTH: Record<GoalHealth, number> = {
  ahead: 0,
  on_track: 0,
  behind: 0,
  at_risk: 0,
};

/**
 * Progress vs pacing, as two stacked bars.
 *
 * A single "62% of target" bar is not management information — it reads as good
 * in week one and as a crisis in the final week. Showing elapsed time underneath
 * is what makes the number actionable. Both values are computed server-side.
 */
function GoalBar({ goal }: { goal: ApiScoredGoal }) {
  const tone =
    goal.health === 'ahead' || goal.health === 'on_track'
      ? 'bg-emerald-500'
      : goal.health === 'behind'
        ? 'bg-amber-500'
        : 'bg-rose-500';

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-xs">
        <span className="font-medium text-gray-700">{pct(goal.progress)} of target</span>
        <span className="text-gray-400">{pct(goal.pacing)} of time elapsed</span>
      </div>
      <div className="h-2 w-full overflow-hidden rounded-full bg-gray-100">
        <div
          className={`h-full rounded-full ${tone}`}
          style={{ width: `${Math.min(100, Math.max(0, goal.progress * 100))}%` }}
        />
      </div>
      <div className="h-1 w-full overflow-hidden rounded-full bg-gray-50">
        <div
          className="h-full rounded-full bg-gray-300"
          style={{ width: `${Math.min(100, Math.max(0, goal.pacing * 100))}%` }}
        />
      </div>
    </div>
  );
}

function InsightTile({
  label,
  value,
  meta,
  bar,
  icon: Icon,
  tone,
  onClick,
}: {
  label: string;
  value: string;
  /** Compact secondary facts — one line, no wrapping essays. */
  meta?: string[];
  /** Optional 0–1 fill for burn / health. */
  bar?: { value: number; className: string };
  icon: ComponentType<{ className?: string }>;
  tone: string;
  onClick?: () => void;
}) {
  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      className={cn(
        'flex h-full min-w-0 flex-col rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-left',
        onClick && 'transition hover:border-blue-300 hover:bg-blue-50/40',
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
          {label}
        </span>
        <span className={cn('flex h-6 w-6 items-center justify-center rounded-md', tone)}>
          <Icon className="h-3.5 w-3.5" />
        </span>
      </div>
      <div className="mt-1 text-2xl font-semibold tabular-nums leading-none text-gray-900">
        {value}
      </div>
      {bar && (
        <div className="mt-2 h-1 w-full overflow-hidden rounded-full bg-gray-100">
          <div
            className={cn('h-full rounded-full', bar.className)}
            style={{ width: `${Math.min(100, Math.max(0, bar.value * 100))}%` }}
          />
        </div>
      )}
      {meta && meta.length > 0 && (
        <p
          className="mt-1.5 truncate text-[11px] leading-snug text-gray-500"
          title={meta.join(' · ')}
        >
          {meta.join(' · ')}
        </p>
      )}
    </Tag>
  );
}

interface PlayFormState {
  name: string;
  objective: string;
  pillar: PlayPillar;
  status: PlayStatus;
  start_date: string;
  end_date: string;
  budget: string;
  currency: PlayCurrency;
  verticals: MarketingVertical[];
  tags: MarketingPlayTag[];
}

type PlayFormErrors = Partial<Record<keyof PlayFormState, string>>;

const today = () => new Date().toISOString().slice(0, 10);
const inDays = (n: number) =>
  new Date(Date.now() + n * 86_400_000).toISOString().slice(0, 10);

const emptyPlayForm: PlayFormState = {
  name: '',
  objective: '',
  pillar: 'brand',
  status: 'draft',
  start_date: today(),
  end_date: inDays(90),
  budget: '',
  currency: 'GHS',
  verticals: [],
  tags: [],
};

const VERTICAL_CHIP_OPTIONS = MARKETING_VERTICALS.map((v) => ({
  value: v,
  label: MARKETING_VERTICAL_LABEL[v],
}));

const TAG_CHIP_OPTIONS = MARKETING_PLAY_TAGS.map((t) => ({
  value: t,
  label: MARKETING_PLAY_TAG_LABEL[t],
}));

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

function validatePlayForm(form: PlayFormState): PlayFormErrors {
  const errors: PlayFormErrors = {};
  const name = form.name.trim();
  if (!name) {
    errors.name = 'Name is required';
  } else if (name.length < 3) {
    errors.name = 'Use at least 3 characters';
  } else if (name.length > 120) {
    errors.name = 'Keep the name under 120 characters';
  }

  if (form.objective.trim().length > 2000) {
    errors.objective = 'Objective is too long (max 2000 characters)';
  }

  if (!(form.pillar in PLAY_PILLAR_LABEL)) {
    errors.pillar = 'Pick a pillar from the list';
  }
  if (!(form.status in PLAY_STATUS_LABEL)) {
    errors.status = 'Pick a status from the list';
  }

  if (!form.start_date || !DATE_RE.test(form.start_date)) {
    errors.start_date = 'Start date is required';
  }
  if (!form.end_date || !DATE_RE.test(form.end_date)) {
    errors.end_date = 'End date is required';
  }
  if (
    !errors.start_date &&
    !errors.end_date &&
    form.end_date < form.start_date
  ) {
    // Pacing divides by the window — an inverted range yields nonsense health.
    errors.end_date = 'End must be on or after the start date';
  }

  const budgetRaw = form.budget.trim().replace(/,/g, '');
  if (budgetRaw) {
    const n = Number(budgetRaw);
    if (!Number.isFinite(n)) {
      errors.budget = 'Budget must be a number';
    } else if (n < 0) {
      errors.budget = 'Budget cannot be negative';
    } else if (n > 1_000_000_000) {
      errors.budget = 'Budget is unrealistically large';
    }
  }

  const currency = form.currency;
  if (budgetRaw && !currency) {
    errors.currency = 'Currency is required when a budget is set';
  } else if (currency && !(PLAY_CURRENCIES as string[]).includes(currency)) {
    errors.currency = 'Pick a currency from the list';
  }

  const badVertical = form.verticals.find((v) => !(MARKETING_VERTICALS as string[]).includes(v));
  if (badVertical) {
    errors.verticals = `Unknown vertical: ${badVertical}`;
  }
  const badTag = form.tags.find((t) => !(MARKETING_PLAY_TAGS as string[]).includes(t));
  if (badTag) {
    errors.tags = `Unknown tag: ${badTag}`;
  }

  return errors;
}

interface GoalFormState {
  metric: string;
  cadence: GoalCadence;
  unit: GoalUnit;
  source_of_truth: string;
  baseline: string;
  target: string;
  current_value: string;
  period_start: string;
  period_end: string;
  play_ids: number[];
}

type GoalFormErrors = Partial<Record<keyof GoalFormState, string>>;

const emptyGoalForm = (): GoalFormState => ({
  metric: '',
  cadence: 'monthly',
  unit: 'number',
  source_of_truth: '',
  baseline: '0',
  target: '',
  current_value: '0',
  period_start: today(),
  period_end: inDays(90),
  play_ids: [],
});

function parseOptionalNumber(raw: string): number | null {
  const t = raw.trim().replace(/,/g, '');
  if (!t) return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : NaN;
}

function validateGoalForm(form: GoalFormState): GoalFormErrors {
  const errors: GoalFormErrors = {};
  const metric = form.metric.trim();
  if (!metric) {
    errors.metric = 'Metric is required';
  } else if (metric.length < 2) {
    errors.metric = 'Use at least 2 characters';
  } else if (metric.length > 120) {
    errors.metric = 'Keep the metric under 120 characters';
  }

  if (!(GOAL_CADENCES as string[]).includes(form.cadence)) {
    errors.cadence = 'Pick a cadence from the list';
  }
  if (!(GOAL_UNITS as string[]).includes(form.unit)) {
    errors.unit = 'Pick a unit from the list';
  }

  if (form.source_of_truth.trim().length > 200) {
    errors.source_of_truth = 'Keep source of truth under 200 characters';
  }

  const baseline = parseOptionalNumber(form.baseline);
  const target = parseOptionalNumber(form.target);
  const current = parseOptionalNumber(form.current_value);

  if (baseline == null) {
    errors.baseline = 'Baseline is required';
  } else if (Number.isNaN(baseline)) {
    errors.baseline = 'Baseline must be a number';
  }

  if (target == null) {
    errors.target = 'Target is required';
  } else if (Number.isNaN(target)) {
    errors.target = 'Target must be a number';
  }

  if (current == null) {
    errors.current_value = 'Current value is required';
  } else if (Number.isNaN(current)) {
    errors.current_value = 'Current value must be a number';
  }

  if (!form.period_start || !DATE_RE.test(form.period_start)) {
    errors.period_start = 'Period start is required';
  }
  if (!form.period_end || !DATE_RE.test(form.period_end)) {
    errors.period_end = 'Period end is required';
  }
  if (
    !errors.period_start &&
    !errors.period_end &&
    form.period_end < form.period_start
  ) {
    errors.period_end = 'Period end must be on or after the start';
  }

  return errors;
}

export default function PlaysPage({
  initialTab = 'campaigns',
}: {
  initialTab?: 'campaigns' | 'goals' | 'plays';
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const { data, isLoading, isError, error } = usePlaysOverviewQuery();
  const createPlay = useCreatePlayMutation();
  const createGoal = useCreateGoalMutation();
  const updatePlay = useUpdatePlayMutation();
  const deletePlay = useDeletePlayMutation();
  const updateGoal = useUpdateGoalMutation();
  const [linkingGoalId, setLinkingGoalId] = useState<number | null>(null);

  // Derive from URL so sidebar links (/campaigns vs /campaigns/goals) switch the
  // tab even when React reuses this component instance.
  const tab: 'campaigns' | 'goals' = location.pathname.endsWith('/goals')
    ? 'goals'
    : initialTab === 'goals'
      ? 'goals'
      : 'campaigns';

  const setTab = (next: 'campaigns' | 'goals') => {
    navigate(next === 'goals' ? '/admin/marketing/campaigns/goals' : '/admin/marketing/campaigns');
  };

  const [campaignSearch, setCampaignSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<PlayStatus | 'all'>('all');
  const [pillarFilter, setPillarFilter] = useState<PlayPillar | 'all'>('all');
  const [campaignSort, setCampaignSort] = useState<CampaignSort>('status');
  const [campaignPage, setCampaignPage] = useState(1);
  const [campaignLimit, setCampaignLimit] = useState(5);

  const [goalSearch, setGoalSearch] = useState('');
  const [goalCampaignFilter, setGoalCampaignFilter] = useState<string>('all');
  const [goalHealthFilter, setGoalHealthFilter] = useState<'all' | GoalHealth>('all');
  const [goalCadenceFilter, setGoalCadenceFilter] = useState<GoalCadence | 'all'>('all');
  const [goalSort, setGoalSort] = useState<GoalSort>('health');
  const [goalPage, setGoalPage] = useState(1);
  const [goalLimit, setGoalLimit] = useState(10);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState<PlayFormState>(emptyPlayForm);
  const [formErrors, setFormErrors] = useState<PlayFormErrors>({});
  const [goalDialogOpen, setGoalDialogOpen] = useState(false);
  const [goalForm, setGoalForm] = useState<GoalFormState>(emptyGoalForm);
  const [goalFormErrors, setGoalFormErrors] = useState<GoalFormErrors>({});
  const [deleting, setDeleting] = useState<ApiPlayWithRollup | null>(null);
  const [linksFor, setLinksFor] = useState<ApiPlayWithRollup | null>(null);

  const patchForm = <K extends keyof PlayFormState>(key: K, value: PlayFormState[K]) => {
    setForm((f) => ({ ...f, [key]: value }));
    setFormErrors((errs) => clearFieldError(errs, key));
  };

  const patchGoalForm = <K extends keyof GoalFormState>(key: K, value: GoalFormState[K]) => {
    setGoalForm((f) => ({ ...f, [key]: value }));
    setGoalFormErrors((errs) => clearFieldError(errs, key));
  };

  const openCreateDialog = () => {
    setForm(emptyPlayForm);
    setFormErrors({});
    setDialogOpen(true);
  };

  const closeCreateDialog = () => {
    setDialogOpen(false);
    setFormErrors({});
  };

  const openCreateGoalDialog = () => {
    setGoalForm(emptyGoalForm());
    setGoalFormErrors({});
    setGoalDialogOpen(true);
  };

  const closeCreateGoalDialog = () => {
    setGoalDialogOpen(false);
    setGoalFormErrors({});
  };

  const plays: ApiPlayWithRollup[] = data?.plays ?? [];
  const goals: ApiScoredGoal[] = data?.goals ?? [];
  const playName = (id: number | null) =>
    plays.find((p) => p.play_id === id)?.name ?? (id == null ? null : `Campaign ${id}`);
  const campaignLabel = (id: number | null) => playName(id) ?? 'Unlinked';

  const goalsByPlay = useMemo(() => {
    const map = new Map<number, ApiScoredGoal[]>();
    goals.forEach((g) => {
      for (const pid of goalPlayIds(g)) {
        map.set(pid, [...(map.get(pid) ?? []), g]);
      }
    });
    return map;
  }, [goals]);

  const goalLinkCounts = useMemo(() => {
    let linked = 0;
    let unlinked = 0;
    for (const g of goals) {
      if (goalPlayIds(g).length === 0) unlinked += 1;
      else linked += 1;
    }
    return { linked, unlinked, total: goals.length };
  }, [goals]);

  const filteredCampaigns = useMemo(() => {
    const q = campaignSearch.trim().toLowerCase();
    const list = plays.filter((play) => {
      if (statusFilter !== 'all' && play.status !== statusFilter) return false;
      if (pillarFilter !== 'all' && play.pillar !== pillarFilter) return false;
      if (
        q &&
        !(
          play.name.toLowerCase().includes(q) ||
          (play.objective ?? '').toLowerCase().includes(q) ||
          asArray(play.verticals).some((v) => v.toLowerCase().includes(q)) ||
          asArray(play.tags).some((t) => t.toLowerCase().includes(q))
        )
      ) {
        return false;
      }
      return true;
    });

    list.sort((a, b) => {
      switch (campaignSort) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'start':
          return (a.start_date ?? '').localeCompare(b.start_date ?? '');
        case 'end':
          return (a.end_date ?? '').localeCompare(b.end_date ?? '');
        case 'spend':
          return (b.spend_amount ?? 0) - (a.spend_amount ?? 0);
        case 'updated':
          return (b.date_modified ?? '').localeCompare(a.date_modified ?? '');
        case 'status':
        default: {
          const byStatus = (STATUS_RANK[a.status] ?? 9) - (STATUS_RANK[b.status] ?? 9);
          return byStatus !== 0 ? byStatus : a.name.localeCompare(b.name);
        }
      }
    });
    return list;
  }, [plays, campaignSearch, statusFilter, pillarFilter, campaignSort]);

  const filteredGoals = useMemo(() => {
    const q = goalSearch.trim().toLowerCase();
    const list = goals.filter((goal) => {
      const ids = goalPlayIds(goal);
      if (goalCampaignFilter === 'none' && ids.length > 0) return false;
      if (goalCampaignFilter === 'linked' && ids.length === 0) return false;
      if (
        goalCampaignFilter !== 'all' &&
        goalCampaignFilter !== 'none' &&
        goalCampaignFilter !== 'linked' &&
        !ids.includes(Number(goalCampaignFilter))
      ) {
        return false;
      }
      if (goalHealthFilter !== 'all' && goal.health !== goalHealthFilter) return false;
      if (goalCadenceFilter !== 'all' && goal.cadence !== goalCadenceFilter) return false;
      if (
        q &&
        !(
          goal.metric.toLowerCase().includes(q) ||
          (goal.source_of_truth ?? '').toLowerCase().includes(q) ||
          ids.some((id) => campaignLabel(id).toLowerCase().includes(q)) ||
          (ids.length === 0 && 'unlinked'.includes(q))
        )
      ) {
        return false;
      }
      return true;
    });

    list.sort((a, b) => {
      switch (goalSort) {
        case 'metric':
          return a.metric.localeCompare(b.metric);
        case 'campaign': {
          const aIds = goalPlayIds(a);
          const bIds = goalPlayIds(b);
          return (
            Number(aIds.length > 0) - Number(bIds.length > 0) ||
            campaignLabel(aIds[0] ?? null).localeCompare(campaignLabel(bIds[0] ?? null)) ||
            a.metric.localeCompare(b.metric)
          );
        }
        case 'progress':
          return b.progress - a.progress;
        case 'cadence':
          return a.cadence.localeCompare(b.cadence) || a.metric.localeCompare(b.metric);
        case 'health':
        default: {
          const byHealth = HEALTH_RANK[a.health] - HEALTH_RANK[b.health];
          return byHealth !== 0 ? byHealth : a.metric.localeCompare(b.metric);
        }
      }
    });
    return list;
  }, [goals, plays, goalSearch, goalCampaignFilter, goalHealthFilter, goalCadenceFilter, goalSort]);

  const campaignStatusOptions = useMemo(
    () => [
      { value: 'all', label: 'All statuses' },
      ...(Object.keys(PLAY_STATUS_LABEL) as PlayStatus[]).map((s) => ({
        value: s,
        label: PLAY_STATUS_LABEL[s],
      })),
    ],
    [],
  );

  const campaignPillarOptions = useMemo(
    () => [
      { value: 'all', label: 'All pillars' },
      ...(Object.keys(PLAY_PILLAR_LABEL) as PlayPillar[]).map((p) => ({
        value: p,
        label: PLAY_PILLAR_LABEL[p],
      })),
    ],
    [],
  );

  const campaignSortOptions = useMemo(
    () => [
      { value: 'status', label: 'Sort: Status' },
      { value: 'name', label: 'Sort: Name' },
      { value: 'start', label: 'Sort: Start date' },
      { value: 'end', label: 'Sort: End date' },
      { value: 'spend', label: 'Sort: Spend' },
      { value: 'updated', label: 'Sort: Recently updated' },
    ],
    [],
  );

  const goalCampaignOptions = useMemo(
    () => [
      { value: 'all', label: 'All campaigns' },
      { value: 'linked', label: 'Linked only' },
      { value: 'none', label: 'Unlinked only' },
      ...plays.map((p) => ({ value: String(p.play_id), label: p.name })),
    ],
    [plays],
  );

  const goalHealthOptions = useMemo(
    () => [
      { value: 'all', label: 'All health' },
      ...(Object.keys(GOAL_HEALTH_LABEL) as GoalHealth[]).map((h) => ({
        value: h,
        label: GOAL_HEALTH_LABEL[h],
      })),
    ],
    [],
  );

  const goalCadenceOptions = useMemo(
    () => [
      { value: 'all', label: 'All cadences' },
      { value: 'weekly', label: 'Weekly' },
      { value: 'monthly', label: 'Monthly' },
      { value: 'quarterly', label: 'Quarterly' },
    ],
    [],
  );

  const goalSortOptions = useMemo(
    () => [
      { value: 'health', label: 'Sort: Health' },
      { value: 'progress', label: 'Sort: Progress' },
      { value: 'metric', label: 'Sort: Metric' },
      { value: 'campaign', label: 'Sort: Campaign' },
      { value: 'cadence', label: 'Sort: Cadence' },
    ],
    [],
  );

  const campaignFiltersActive =
    campaignSearch.trim().length > 0 ||
    statusFilter !== 'all' ||
    pillarFilter !== 'all' ||
    campaignSort !== 'status';

  const goalFiltersActive =
    goalSearch.trim().length > 0 ||
    goalCampaignFilter !== 'all' ||
    goalHealthFilter !== 'all' ||
    goalCadenceFilter !== 'all' ||
    goalSort !== 'health';

  const clearCampaignFilters = () => {
    setCampaignSearch('');
    setStatusFilter('all');
    setPillarFilter('all');
    setCampaignSort('status');
  };

  const clearGoalFilters = () => {
    setGoalSearch('');
    setGoalCampaignFilter('all');
    setGoalHealthFilter('all');
    setGoalCadenceFilter('all');
    setGoalSort('health');
  };

  useEffect(() => {
    setCampaignPage(1);
  }, [campaignSearch, statusFilter, pillarFilter, campaignSort]);

  useEffect(() => {
    setGoalPage(1);
  }, [goalSearch, goalCampaignFilter, goalHealthFilter, goalCadenceFilter, goalSort]);

  useEffect(() => {
    const max = Math.max(1, Math.ceil(filteredCampaigns.length / campaignLimit) || 1);
    if (campaignPage > max) setCampaignPage(max);
  }, [filteredCampaigns.length, campaignLimit, campaignPage]);

  useEffect(() => {
    const max = Math.max(1, Math.ceil(filteredGoals.length / goalLimit) || 1);
    if (goalPage > max) setGoalPage(max);
  }, [filteredGoals.length, goalLimit, goalPage]);

  const pagedCampaigns = useMemo(() => {
    const start = (campaignPage - 1) * campaignLimit;
    return filteredCampaigns.slice(start, start + campaignLimit);
  }, [filteredCampaigns, campaignPage, campaignLimit]);

  const pagedGoals = useMemo(() => {
    const start = (goalPage - 1) * goalLimit;
    return filteredGoals.slice(start, start + goalLimit);
  }, [filteredGoals, goalPage, goalLimit]);

  const insights = useMemo(() => {
    const active = plays.filter((p) => p.status === 'active');
    const totalBudget = plays.reduce((s, p) => s + (p.budget_amount ?? 0), 0);
    const totalSpend = plays.reduce((s, p) => s + (p.spend_amount ?? 0), 0);
    const budgetUtil = totalBudget > 0 ? totalSpend / totalBudget : 0;

    const healthCounts = { ...EMPTY_HEALTH };
    for (const g of goals) healthCounts[g.health] += 1;
    const needsAttention = healthCounts.behind + healthCounts.at_risk;
    const avgProgress =
      goals.length === 0 ? 0 : goals.reduce((s, g) => s + g.progress, 0) / goals.length;

    const linked = plays.reduce(
      (acc, p) => ({
        content: acc.content + p.rollup.content,
        social: acc.social + p.rollup.social,
        traditional: acc.traditional + p.rollup.traditional,
        outreach: acc.outreach + p.rollup.outreach,
      }),
      { content: 0, social: 0, traditional: 0, outreach: 0 },
    );
    const unlinkedGoals = data?.unlinked?.goals ?? 0;
    const campaignsWithoutGoals = active.filter((p) => p.rollup.goals === 0).length;

    // Active plays whose spend share is ahead of calendar elapsed.
    const now = Date.now();
    const overBurn = active.filter((p) => {
      if (!p.budget_amount || !p.spend_amount) return false;
      const start = new Date(p.start_date).getTime();
      const end = new Date(p.end_date).getTime();
      if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return false;
      const timeElapsed = Math.min(1, Math.max(0, (now - start) / (end - start)));
      return p.spend_amount / p.budget_amount > timeElapsed + 0.15;
    });

    const worstGoals = [...goals]
      .filter((g) => g.health === 'at_risk' || g.health === 'behind')
      .sort((a, b) => HEALTH_RANK[a.health] - HEALTH_RANK[b.health] || a.progress - b.progress)
      .slice(0, 3);

    const topSpend = [...active]
      .filter((p) => (p.spend_amount ?? 0) > 0)
      .sort((a, b) => (b.spend_amount ?? 0) - (a.spend_amount ?? 0))
      .slice(0, 3);

    const callouts: { tone: string; text: string; action?: () => void }[] = [];
    if (needsAttention > 0) {
      callouts.push({
        tone: 'border-rose-200 bg-rose-50 text-rose-900',
        text: `${needsAttention} goal${needsAttention === 1 ? '' : 's'} behind or at risk — pace is slipping vs the calendar.`,
        action: () => {
          setTab('goals');
          setGoalHealthFilter('at_risk');
          setGoalSort('health');
        },
      });
    }
    if (overBurn.length > 0) {
      callouts.push({
        tone: 'border-amber-200 bg-amber-50 text-amber-900',
        text: `${overBurn.length} active campaign${overBurn.length === 1 ? '' : 's'} burning budget ahead of schedule (${overBurn.map((p) => p.name).join(', ')}).`,
        action: () => {
          setTab('campaigns');
          setStatusFilter('active');
          setCampaignSort('spend');
        },
      });
    }
    if (campaignsWithoutGoals > 0) {
      callouts.push({
        tone: 'border-sky-200 bg-sky-50 text-sky-900',
        text: `${campaignsWithoutGoals} active campaign${campaignsWithoutGoals === 1 ? '' : 's'} have no goals — “did it work?” can’t be answered.`,
        action: () => setTab('goals'),
      });
    }
    if (unlinkedGoals > 0) {
      callouts.push({
        tone: 'border-violet-200 bg-violet-50 text-violet-900',
        text: `${unlinkedGoals} goal${unlinkedGoals === 1 ? '' : 's'} aren’t linked to a campaign.`,
        action: () => {
          setTab('goals');
          setGoalCampaignFilter('none');
        },
      });
    }
    if (callouts.length === 0 && plays.length > 0) {
      callouts.push({
        tone: 'border-emerald-200 bg-emerald-50 text-emerald-900',
        text: 'Goals are pacing with the calendar and spend looks controlled. Keep shipping against active campaigns.',
      });
    }

    return {
      activeCount: active.length,
      totalCount: plays.length,
      totalBudget,
      totalSpend,
      budgetUtil,
      healthCounts,
      needsAttention,
      avgProgress,
      linked,
      pipelineTotal: linked.content + linked.social + linked.traditional + linked.outreach,
      worstGoals,
      topSpend,
      callouts,
    };
  }, [plays, goals, data]);

  const submitPlay = (e: React.FormEvent) => {
    e.preventDefault();
    const errors = validatePlayForm(form);
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) {
      toast.error('Fix the highlighted fields before creating');
      return;
    }

    const budgetMajor = form.budget.trim().replace(/,/g, '');
    const currency = form.currency || 'GHS';

    createPlay.mutate(
      {
        name: form.name.trim(),
        objective: form.objective.trim() || null,
        pillar: form.pillar,
        status: form.status,
        start_date: form.start_date,
        end_date: form.end_date,
        // Money is stored in minor units so there is no float drift.
        budget_amount: budgetMajor ? Math.round(Number(budgetMajor) * 100) : null,
        currency,
        verticals: form.verticals,
        tags: form.tags,
      },
      {
        onSuccess: () => {
          toast.success(`Created “${form.name.trim()}”`);
          setForm(emptyPlayForm);
          setFormErrors({});
          setDialogOpen(false);
        },
        onError: (err) => toast.error(getApiErrorMessage(err, 'Could not create the campaign')),
      },
    );
  };

  const submitGoal = (e: React.FormEvent) => {
    e.preventDefault();
    const errors = validateGoalForm(goalForm);
    setGoalFormErrors(errors);
    if (Object.keys(errors).length > 0) {
      toast.error('Fix the highlighted fields before creating');
      return;
    }

    const playIds = [...new Set(goalForm.play_ids.filter((n) => Number.isFinite(n)))];
    createGoal.mutate(
      {
        metric: goalForm.metric.trim(),
        cadence: goalForm.cadence,
        unit: goalForm.unit,
        source_of_truth: goalForm.source_of_truth.trim() || null,
        baseline: parseOptionalNumber(goalForm.baseline) ?? 0,
        target: parseOptionalNumber(goalForm.target) ?? 0,
        current_value: parseOptionalNumber(goalForm.current_value) ?? 0,
        period_start: goalForm.period_start,
        period_end: goalForm.period_end,
        play_ids: playIds,
        play_id: playIds[0] ?? null,
      },
      {
        onSuccess: () => {
          toast.success(`Created goal “${goalForm.metric.trim()}”`);
          setGoalForm(emptyGoalForm());
          setGoalFormErrors({});
          setGoalDialogOpen(false);
        },
        onError: (err) => toast.error(getApiErrorMessage(err, 'Could not create the goal')),
      },
    );
  };

  const changeStatus = (play: ApiPlayWithRollup, status: string) => {
    updatePlay.mutate(
      { play_id: play.play_id, status },
      { onError: (err) => toast.error(getApiErrorMessage(err, 'Could not update the campaign')) },
    );
  };

  const linkGoal = (goalId: number, playIds: number[]) => {
    setLinkingGoalId(goalId);
    const unique = [...new Set(playIds.filter((n) => Number.isFinite(n)))];
    updateGoal.mutate(
      {
        goal_id: goalId,
        play_ids: unique,
        play_id: unique[0] ?? null,
      },
      {
        onSuccess: () =>
          toast.success(
            unique.length === 0
              ? 'Goal unlinked from all campaigns'
              : unique.length === 1
                ? 'Goal linked to campaign'
                : `Goal linked to ${unique.length} campaigns`,
          ),
        onError: (err) =>
          toast.error(getApiErrorMessage(err, 'Could not update the goal link')),
        onSettled: () => setLinkingGoalId(null),
      },
    );
  };

  const attachGoalToPlay = (goalId: number, playId: number) => {
    const goal = goals.find((g) => g.goal_id === goalId);
    if (!goal) return;
    const ids = goalPlayIds(goal);
    if (ids.includes(playId)) return;
    linkGoal(goalId, [...ids, playId]);
  };

  const detachGoalFromPlay = (goalId: number, playId: number) => {
    const goal = goals.find((g) => g.goal_id === goalId);
    if (!goal) return;
    linkGoal(
      goalId,
      goalPlayIds(goal).filter((id) => id !== playId),
    );
  };

  const goalsNotOnPlay = useMemo(() => {
    const byPlay = new Map<number, ApiScoredGoal[]>();
    for (const play of plays) {
      byPlay.set(
        play.play_id,
        goals
          .filter((g) => !goalPlayIds(g).includes(play.play_id))
          .sort((a, b) => a.metric.localeCompare(b.metric)),
      );
    }
    return byPlay;
  }, [goals, plays]);

  const unlinkedGoalsList = useMemo(
    () => goals.filter((g) => goalPlayIds(g).length === 0).sort((a, b) => a.metric.localeCompare(b.metric)),
    [goals],
  );

  const confirmDelete = () => {
    if (!deleting) return;
    const name = deleting.name;
    deletePlay.mutate(
      { play_id: deleting.play_id },
      {
        onSuccess: () => toast.success(`Removed ${name}`),
        onError: (err) => toast.error(getApiErrorMessage(err, 'Could not remove the campaign')),
      },
    );
    setDeleting(null);
  };

  if (isError) {
    return (
      <MarketingPageShell
        title="Campaigns"
        description="The strategy spine. Every piece of content, post, placement and mention hangs off a campaign, so 'did it work?' has an answer."
        crumbs={[{ label: 'Marketing', href: '/admin/marketing' }, { label: 'Campaigns' }]}
        icon={Flag}
      >
        <div className="rounded-xl border border-amber-100 bg-amber-50/40 px-6 py-16 text-center text-sm shadow-sm">
          <WifiOff className="mb-2 inline h-5 w-5 text-amber-600" />
          <div className="font-medium text-amber-800">Could not load campaigns</div>
          <div className="mt-1 text-xs text-gray-500">
            {getApiErrorMessage(error, 'The marketing API did not respond')}
          </div>
        </div>
      </MarketingPageShell>
    );
  }

  return (
    <MarketingPageShell
      title="Campaigns"
      description="The strategy spine. Every piece of content, post, placement and mention hangs off a campaign, so 'did it work?' has an answer."
      crumbs={[{ label: 'Marketing', href: '/admin/marketing' }, { label: 'Campaigns' }]}
      icon={Flag}
      actions={
        <Button
          type="button"
          className="gap-2"
          onClick={tab === 'goals' ? openCreateGoalDialog : openCreateDialog}
        >
          <Plus className="h-4 w-4" />
          {tab === 'goals' ? 'New goal' : 'New campaign'}
        </Button>
      }
    >
      {isLoading ? (
        <div className="rounded-xl border border-gray-100 bg-white px-6 py-16 text-center text-sm text-gray-500 shadow-sm">
          <Loader2 className="mr-2 inline h-4 w-4 animate-spin" />
          Loading campaigns…
        </div>
      ) : (
        <>
          <div className="mb-6 space-y-4">
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
              <InsightTile
                label="Active"
                value={`${insights.activeCount}/${insights.totalCount}`}
                meta={[
                  `${plays.filter((p) => p.status === 'draft').length} draft`,
                  `${plays.filter((p) => p.status === 'paused').length} paused`,
                ]}
                icon={Flag}
                tone="bg-blue-50 text-blue-700"
                onClick={() => {
                  setTab('campaigns');
                  setStatusFilter('active');
                }}
              />
              <InsightTile
                label="Goal health"
                value={String(insights.needsAttention)}
                meta={['at risk / behind', `avg ${pct(insights.avgProgress)}`]}
                bar={{
                  value: goals.length
                    ? (insights.healthCounts.ahead + insights.healthCounts.on_track) / goals.length
                    : 0,
                  className: insights.needsAttention > 0 ? 'bg-rose-500' : 'bg-emerald-500',
                }}
                icon={Target}
                tone={
                  insights.needsAttention > 0
                    ? 'bg-rose-50 text-rose-700'
                    : 'bg-emerald-50 text-emerald-700'
                }
                onClick={() => {
                  setTab('goals');
                  setGoalSort('health');
                  if (insights.healthCounts.at_risk > 0) setGoalHealthFilter('at_risk');
                  else if (insights.healthCounts.behind > 0) setGoalHealthFilter('behind');
                }}
              />
              <InsightTile
                label="Budget burn"
                value={pct(insights.budgetUtil)}
                meta={[money(insights.totalSpend, 'GHS'), `of ${money(insights.totalBudget, 'GHS')}`]}
                bar={{
                  value: insights.budgetUtil,
                  className: insights.budgetUtil > 0.85 ? 'bg-amber-500' : 'bg-violet-500',
                }}
                icon={Wallet}
                tone={
                  insights.budgetUtil > 0.85
                    ? 'bg-amber-50 text-amber-700'
                    : 'bg-violet-50 text-violet-700'
                }
                onClick={() => {
                  setTab('campaigns');
                  setCampaignSort('spend');
                }}
              />
              <InsightTile
                label="Attributed"
                value={String(insights.pipelineTotal)}
                meta={[
                  `${insights.linked.content} content`,
                  `${insights.linked.social + insights.linked.traditional} queue`,
                  `${insights.linked.outreach} outreach`,
                ]}
                icon={TrendingUp}
                tone="bg-sky-50 text-sky-700"
              />
            </div>

            <div className="grid gap-4 lg:grid-cols-[1.2fr_1fr]">
              <div className="rounded-lg border border-gray-200 bg-white p-4">
                <div className="mb-3 flex items-center justify-between gap-2">
                  <h2 className="text-sm font-semibold text-gray-900">Insights</h2>
                  <span className="text-[11px] text-gray-400">Computed server-side</span>
                </div>
                {insights.callouts.length === 0 ? (
                  <p className="text-xs text-gray-400">
                    Nothing to report yet — create a campaign and give it goals.
                  </p>
                ) : (
                  <ul className="space-y-2">
                    {insights.callouts.map((c, i) => (
                      <li key={i}>
                        <button
                          type="button"
                          disabled={!c.action}
                          onClick={c.action}
                          className={cn(
                            'w-full rounded-md border px-3 py-2 text-left text-sm',
                            c.tone,
                            c.action && 'hover:opacity-90',
                            !c.action && 'cursor-default',
                          )}
                        >
                          {c.text}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                {insights.worstGoals.length > 0 && (
                  <div className="mt-4 border-t border-gray-100 pt-3">
                    <div className="mb-2 text-[11px] font-medium uppercase tracking-wide text-gray-400">
                      Goals slipping
                    </div>
                    <ul className="space-y-2">
                      {insights.worstGoals.map((g) => (
                        <li key={g.goal_id} className="flex items-start justify-between gap-3 text-sm">
                          <div className="min-w-0">
                            <p className="truncate font-medium text-gray-900">{g.metric}</p>
                            <p className="truncate text-xs text-gray-500">
                              {(() => {
                                const ids = goalPlayIds(g);
                                if (ids.length === 0) return 'Unlinked';
                                return ids.map((id) => playName(id) ?? `Campaign ${id}`).join(' · ');
                              })()}
                            </p>
                          </div>
                          <StatusBadge
                            label={GOAL_HEALTH_LABEL[g.health]}
                            className={GOAL_HEALTH_BADGE[g.health]}
                          />
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              <div className="space-y-4">
                <div className="rounded-lg border border-gray-200 bg-white p-4">
                  <div className="mb-2 flex items-center justify-between">
                    <h2 className="text-sm font-semibold text-gray-900">Goal health mix</h2>
                    <AlertTriangle className="h-3.5 w-3.5 text-gray-400" />
                  </div>
                  <div className="mb-2 flex h-2.5 overflow-hidden rounded-full bg-gray-100">
                    {(Object.keys(HEALTH_BAR) as GoalHealth[]).map((h) => {
                      const n = insights.healthCounts[h];
                      if (!n || goals.length === 0) return null;
                      return (
                        <div
                          key={h}
                          className={HEALTH_BAR[h]}
                          style={{ width: `${(n / goals.length) * 100}%` }}
                          title={`${GOAL_HEALTH_LABEL[h]}: ${n}`}
                        />
                      );
                    })}
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
                    {(Object.keys(GOAL_HEALTH_LABEL) as GoalHealth[]).map((h) => (
                      <button
                        key={h}
                        type="button"
                        onClick={() => {
                          setTab('goals');
                          setGoalHealthFilter(h);
                        }}
                        className="flex items-center justify-between rounded-md px-1.5 py-1 hover:bg-gray-50"
                      >
                        <span className="inline-flex items-center gap-1.5">
                          <span className={cn('h-2 w-2 rounded-full', HEALTH_BAR[h])} />
                          {GOAL_HEALTH_LABEL[h]}
                        </span>
                        <span className="font-medium text-gray-900">
                          {insights.healthCounts[h]}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="rounded-lg border border-gray-200 bg-white p-4">
                  <h2 className="mb-2 text-sm font-semibold text-gray-900">Top spend (active)</h2>
                  {insights.topSpend.length === 0 ? (
                    <p className="text-xs text-gray-400">No spend recorded on active campaigns.</p>
                  ) : (
                    <ul className="space-y-2.5">
                      {insights.topSpend.map((p) => {
                        const util = p.budget_amount ? (p.spend_amount ?? 0) / p.budget_amount : 0;
                        return (
                          <li key={p.play_id}>
                            <div className="mb-1 flex items-center justify-between gap-2 text-sm">
                              <span className="truncate font-medium text-gray-900">{p.name}</span>
                              <span className="shrink-0 text-xs text-gray-500">{pct(util)}</span>
                            </div>
                            <div className="h-1.5 overflow-hidden rounded-full bg-gray-100">
                              <div
                                className={cn(
                                  'h-full rounded-full',
                                  util > 0.85 ? 'bg-amber-500' : 'bg-violet-500',
                                )}
                                style={{ width: `${Math.min(100, util * 100)}%` }}
                              />
                            </div>
                            <p className="mt-0.5 text-[11px] text-gray-400">
                              {money(p.spend_amount, p.currency)} /{' '}
                              {money(p.budget_amount, p.currency)}
                            </p>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="mb-4 flex gap-1 border-b border-gray-200">
            {(['campaigns', 'goals'] as const).map((t) => (
              <button
                key={t}
                onClick={() => setTab(t)}
                className={`px-4 py-2 text-sm font-medium ${
                  tab === t
                    ? 'border-b-2 border-blue-700 text-blue-700'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {t === 'campaigns' ? 'Campaigns' : 'Goals'}
              </button>
            ))}
          </div>

          {tab === 'campaigns' && (
            <div className="space-y-4">
              <MarketingFilterPanel
                summary={marketingMatchSummary(
                  filteredCampaigns.length,
                  plays.length,
                  'campaign',
                  isLoading,
                )}
                showClear={campaignFiltersActive}
                onClear={clearCampaignFilters}
              >
                <MarketingFilterSearch
                  value={campaignSearch}
                  onChange={setCampaignSearch}
                  placeholder="Search name, objective, tags…"
                />
                <MarketingFilterSelect
                  options={campaignStatusOptions}
                  value={statusFilter}
                  onChange={(value) => setStatusFilter(value as PlayStatus | 'all')}
                  placeholder="All statuses"
                  clearValue="all"
                  className={marketingFilterSelectClass}
                />
                <MarketingFilterSelect
                  options={campaignPillarOptions}
                  value={pillarFilter}
                  onChange={(value) => setPillarFilter(value as PlayPillar | 'all')}
                  placeholder="All pillars"
                  clearValue="all"
                  className={marketingFilterSelectClass}
                />
                <MarketingFilterSelect
                  options={campaignSortOptions}
                  value={campaignSort}
                  onChange={(value) => setCampaignSort(value as CampaignSort)}
                  placeholder="Sort"
                  clearValue="status"
                  className={marketingFilterSelectClass}
                />
              </MarketingFilterPanel>

              {filteredCampaigns.length === 0 ? (
                <EmptyState
                  label={
                    plays.length === 0
                      ? 'No campaigns yet — create one so content, posts and coverage have something to hang off.'
                      : 'No campaigns match these filters'
                  }
                />
              ) : (
                <>
                  <TooltipProvider delayDuration={200}>
                  {pagedCampaigns.map((play) => {
                    const playGoals = goalsByPlay.get(play.play_id) ?? [];
                    const rollup = play.rollup;
                    const verticals = asArray(play.verticals);
                    return (
                      <div
                        key={play.play_id}
                        className="rounded-lg border border-gray-200 bg-white p-5"
                      >
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                              <h3 className="text-base font-semibold text-gray-900">{play.name}</h3>
                              <StatusBadge
                                label={PLAY_STATUS_LABEL[play.status as PlayStatus] ?? play.status}
                                className={PLAY_STATUS_BADGE[play.status as PlayStatus] ?? ''}
                              />
                              <span className="rounded-full border border-gray-200 bg-gray-50 px-2 py-0.5 text-[11px] text-gray-600">
                                {PLAY_PILLAR_LABEL[play.pillar as PlayPillar] ?? play.pillar}
                              </span>
                            </div>
                            {play.objective && (
                              <p className="mt-1 max-w-3xl text-sm text-gray-600">
                                {play.objective}
                              </p>
                            )}
                            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
                              <span>
                                {play.start_date} → {play.end_date}
                              </span>
                              <span>
                                Spend {money(play.spend_amount, play.currency)} /{' '}
                                {money(play.budget_amount, play.currency)}
                              </span>
                              {verticals.length > 0 && (
                                <span>
                                  Verticals:{' '}
                                  {verticals
                                    .map(
                                      (v) =>
                                        MARKETING_VERTICAL_LABEL[v as MarketingVertical] ?? v,
                                    )
                                    .join(', ')}
                                </span>
                              )}
                            </div>
                            {asArray(play.tags).length > 0 && (
                              <div className="mt-2 flex flex-wrap gap-1">
                                {asArray(play.tags).map((t) => (
                                  <span
                                    key={t}
                                    className="rounded-md border border-gray-200 bg-gray-50 px-1.5 py-0.5 text-[10px] font-medium text-gray-600"
                                  >
                                    {MARKETING_PLAY_TAG_LABEL[t as MarketingPlayTag] ?? t}
                                  </span>
                                ))}
                              </div>
                            )}
                            <div className="mt-3 flex flex-wrap gap-x-3 gap-y-1 text-xs">
                              <Link
                                to={`/admin/marketing/studio/content?play=${play.play_id}`}
                                className="font-medium text-blue-700 hover:underline"
                              >
                                {rollup.content} content
                              </Link>
                              <Link
                                to="/admin/marketing/studio/series"
                                className="text-gray-600 hover:underline"
                              >
                                {rollup.series} series
                              </Link>
                              <Link
                                to="/admin/marketing/studio/social"
                                className="text-gray-600 hover:underline"
                              >
                                {rollup.social} social
                              </Link>
                              <Link
                                to="/admin/marketing/studio/traditional"
                                className="text-gray-600 hover:underline"
                              >
                                {rollup.traditional} traditional
                              </Link>
                              <Link
                                to="/admin/marketing/sources/outreach"
                                className="text-gray-600 hover:underline"
                              >
                                {rollup.outreach} outreach
                              </Link>
                              <span className="text-gray-500">{rollup.goals} goals</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <select
                              value={play.status}
                              onChange={(e) => changeStatus(play, e.target.value)}
                              className="rounded-md border border-gray-300 px-2 py-1 text-xs"
                            >
                              {Object.entries(PLAY_STATUS_LABEL).map(([v, l]) => (
                                <option key={v} value={v}>
                                  {l}
                                </option>
                              ))}
                            </select>
                            <ActionIconButton
                              label="Links & results"
                              onClick={() => setLinksFor(play)}
                              className="border border-gray-300 text-gray-500 hover:bg-gray-50 hover:text-gray-900"
                            >
                              <Link2 className="h-4 w-4" />
                            </ActionIconButton>
                            <ActionIconButton
                              label="Delete"
                              onClick={() => setDeleting(play)}
                              className="border border-gray-300 text-gray-500 hover:bg-red-50 hover:text-red-600"
                            >
                              <Trash2 className="h-4 w-4" />
                            </ActionIconButton>
                          </div>
                        </div>

                        {playGoals.length > 0 && (
                          <div className="mt-4 grid gap-4 border-t border-gray-100 pt-4 md:grid-cols-2">
                            {playGoals.map((goal) => (
                              <div key={goal.goal_id}>
                                <div className="mb-1 flex items-center justify-between gap-2">
                                  <span className="truncate text-sm font-medium text-gray-800">
                                    {goal.metric}
                                  </span>
                                  <div className="flex shrink-0 items-center gap-1.5">
                                    <StatusBadge
                                      label={GOAL_HEALTH_LABEL[goal.health]}
                                      className={GOAL_HEALTH_BADGE[goal.health]}
                                    />
                                    <button
                                      type="button"
                                      disabled={linkingGoalId === goal.goal_id}
                                      onClick={() => detachGoalFromPlay(goal.goal_id, play.play_id)}
                                      className="text-[11px] font-medium text-gray-500 hover:text-violet-700 disabled:opacity-50"
                                      title="Unlink from this campaign"
                                    >
                                      Unlink
                                    </button>
                                  </div>
                                </div>
                                <GoalBar goal={goal} />
                                <div className="mt-1 text-xs text-gray-500">
                                  {goal.current_value.toLocaleString()} /{' '}
                                  {goal.target.toLocaleString()} (from{' '}
                                  {goal.baseline.toLocaleString()})
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        <div
                          className={cn(
                            'mt-3 flex flex-wrap items-center gap-2',
                            playGoals.length > 0 ? '' : 'border-t border-gray-100 pt-3',
                          )}
                        >
                          <select
                            defaultValue=""
                            disabled={
                              (goalsNotOnPlay.get(play.play_id)?.length ?? 0) === 0 ||
                              updateGoal.isPending
                            }
                            onChange={(e) => {
                              const goalId = Number(e.target.value);
                              if (!Number.isFinite(goalId)) return;
                              attachGoalToPlay(goalId, play.play_id);
                              e.target.value = '';
                            }}
                            className="h-8 max-w-xs rounded-md border border-dashed border-gray-300 bg-white px-2 text-xs text-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
                            aria-label={`Link a goal to ${play.name}`}
                          >
                            <option value="" disabled>
                              {(goalsNotOnPlay.get(play.play_id)?.length ?? 0) === 0
                                ? 'All goals already linked'
                                : 'Link a goal…'}
                            </option>
                            {(goalsNotOnPlay.get(play.play_id) ?? []).map((g) => (
                              <option key={g.goal_id} value={g.goal_id}>
                                {g.metric}
                                {goalPlayIds(g).length > 0 ? ' (shared)' : ''}
                              </option>
                            ))}
                          </select>
                          {unlinkedGoalsList.length > 0 && (
                            <button
                              type="button"
                              onClick={() => {
                                setTab('goals');
                                setGoalCampaignFilter('none');
                              }}
                              className="text-[11px] font-medium text-blue-700 hover:underline"
                            >
                              View unlinked →
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  </TooltipProvider>
                  <Pagination
                    total={filteredCampaigns.length}
                    page={campaignPage}
                    limit={campaignLimit}
                    itemsPerPage={campaignLimit}
                    setPage={setCampaignPage}
                    setItemsPerPage={(n) => {
                      setCampaignLimit(n);
                      setCampaignPage(1);
                    }}
                    showSelect
                    selectOptions={[5, 10, 20]}
                    compact
                  />
                </>
              )}
            </div>
          )}

          {tab === 'goals' && (
            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => setGoalCampaignFilter('all')}
                  className={cn(
                    'rounded-md border px-2.5 py-1 text-xs font-medium',
                    goalCampaignFilter === 'all'
                      ? 'border-gray-900 bg-gray-900 text-white'
                      : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50',
                  )}
                >
                  All {goalLinkCounts.total}
                </button>
                <button
                  type="button"
                  onClick={() => setGoalCampaignFilter('linked')}
                  className={cn(
                    'rounded-md border px-2.5 py-1 text-xs font-medium',
                    goalCampaignFilter === 'linked'
                      ? 'border-emerald-500 bg-emerald-50 text-emerald-900'
                      : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50',
                  )}
                  title="Goals with a campaign"
                >
                  Linked {goalLinkCounts.linked}
                </button>
                <button
                  type="button"
                  onClick={() => setGoalCampaignFilter('none')}
                  className={cn(
                    'rounded-md border px-2.5 py-1 text-xs font-medium',
                    goalCampaignFilter === 'none'
                      ? 'border-violet-500 bg-violet-50 text-violet-900'
                      : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50',
                  )}
                  title="Goals with no campaign"
                >
                  Unlinked {goalLinkCounts.unlinked}
                </button>
                {goalCampaignFilter === 'none' && (
                  <span className="text-xs text-violet-800">
                    Showing goals with no campaign — assign one so performance has somewhere to land.
                  </span>
                )}
                {goalCampaignFilter === 'linked' && (
                  <span className="text-xs text-emerald-800">
                    Showing goals that are already linked to a campaign.
                  </span>
                )}
                {goalCampaignFilter !== 'all' &&
                  goalCampaignFilter !== 'none' &&
                  goalCampaignFilter !== 'linked' && (
                  <span className="text-xs text-emerald-800">
                    Showing goals linked to{' '}
                    <span className="font-medium">{playName(Number(goalCampaignFilter))}</span>.
                  </span>
                )}
              </div>

              <MarketingFilterPanel
                summary={marketingMatchSummary(filteredGoals.length, goals.length, 'goal', isLoading)}
                showClear={goalFiltersActive}
                onClear={clearGoalFilters}
              >
                <MarketingFilterSearch
                  value={goalSearch}
                  onChange={setGoalSearch}
                  placeholder="Search metric, campaign, source…"
                />
                <MarketingFilterSelect
                  options={goalCampaignOptions}
                  value={goalCampaignFilter}
                  onChange={setGoalCampaignFilter}
                  placeholder="All campaigns"
                  clearValue="all"
                  className={`${marketingFilterSelectClass} sm:min-w-[12rem]`}
                />
                <MarketingFilterSelect
                  options={goalHealthOptions}
                  value={goalHealthFilter}
                  onChange={(value) => setGoalHealthFilter(value as typeof goalHealthFilter)}
                  placeholder="All health"
                  clearValue="all"
                  className={marketingFilterSelectClass}
                />
                <MarketingFilterSelect
                  options={goalCadenceOptions}
                  value={goalCadenceFilter}
                  onChange={(value) => setGoalCadenceFilter(value as GoalCadence | 'all')}
                  placeholder="All cadences"
                  clearValue="all"
                  className={marketingFilterSelectClass}
                />
                <MarketingFilterSelect
                  options={goalSortOptions}
                  value={goalSort}
                  onChange={(value) => setGoalSort(value as GoalSort)}
                  placeholder="Sort"
                  clearValue="health"
                  className={marketingFilterSelectClass}
                />
              </MarketingFilterPanel>

              <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                    <tr>
                      <th className="px-4 py-3">Metric</th>
                      <th className="min-w-[16rem] px-4 py-3">Campaigns</th>
                      <th className="px-4 py-3">Cadence</th>
                      <th className="px-4 py-3">Source of truth</th>
                      <th className="w-56 px-4 py-3">Progress</th>
                      <th className="px-4 py-3">Health</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredGoals.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="px-4 py-12">
                          <EmptyState
                            label={
                              goals.length === 0
                                ? 'No goals yet — a campaign without one cannot answer “did it work?”'
                                : 'No goals match these filters'
                            }
                          />
                        </td>
                      </tr>
                    ) : (
                      pagedGoals.map((goal) => (
                        <tr key={goal.goal_id}>
                          <td className="px-4 py-3 font-medium text-gray-900">{goal.metric}</td>
                          <td className="px-4 py-3">
                            <GoalCampaignLinker
                              playIds={goalPlayIds(goal)}
                              plays={plays}
                              dense
                              busy={linkingGoalId === goal.goal_id}
                              disabled={linkingGoalId != null && linkingGoalId !== goal.goal_id}
                              onChange={(ids) => linkGoal(goal.goal_id, ids)}
                              aria-label={`Campaigns for ${goal.metric}`}
                            />
                          </td>
                          <td className="px-4 py-3 capitalize text-gray-600">{goal.cadence}</td>
                          <td className="px-4 py-3">
                            {goal.source_of_truth ? (
                              <code className="rounded bg-gray-50 px-1.5 py-0.5 text-[11px] text-gray-600">
                                {goal.source_of_truth}
                              </code>
                            ) : (
                              <span className="text-xs text-gray-400">not set</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <GoalBar goal={goal} />
                          </td>
                          <td className="px-4 py-3">
                            <StatusBadge
                              label={GOAL_HEALTH_LABEL[goal.health]}
                              className={GOAL_HEALTH_BADGE[goal.health]}
                            />
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
                <div className="border-t border-gray-100 bg-gray-50 px-4 py-2 text-xs text-gray-500">
                  <TrendingUp className="mr-1 inline h-3 w-3" />
                  Mirrors the KPI Tracker tab of the marketing companion workbook — baseline,
                  target, current and source of truth. Progress and pacing are computed
                  server-side.
                </div>
              </div>
              {filteredGoals.length > 0 && (
                <Pagination
                  total={filteredGoals.length}
                  page={goalPage}
                  limit={goalLimit}
                  itemsPerPage={goalLimit}
                  setPage={setGoalPage}
                  setItemsPerPage={(n) => {
                    setGoalLimit(n);
                    setGoalPage(1);
                  }}
                  showSelect
                  selectOptions={[10, 20, 50]}
                  compact
                />
              )}
            </div>
          )}
        </>
      )}

      <Dialog
        open={dialogOpen}
        onOpenChange={(open) => {
          if (open) setDialogOpen(true);
          else closeCreateDialog();
        }}
      >
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>New campaign</DialogTitle>
            <DialogDescription>
              A campaign is a window with an objective and a budget. Content, posts, placements and
              outreach hang off it, which is what makes “did it work?” answerable.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={submitPlay} className="space-y-4" noValidate>
            <label className="block space-y-1.5">
              <span className="text-xs font-medium text-gray-600">
                Name <RequiredMark />
              </span>
              <input
                value={form.name}
                onChange={(e) => patchForm('name', e.target.value)}
                className={fieldClass(`${inputClass} w-full`, formErrors.name)}
                placeholder="H1 Homes — Accra supply push"
                aria-invalid={!!formErrors.name}
                autoFocus
              />
              <FieldHint error={formErrors.name} />
            </label>

            <label className="block space-y-1.5">
              <span className="text-xs font-medium text-gray-600">Objective</span>
              <textarea
                value={form.objective}
                onChange={(e) => patchForm('objective', e.target.value)}
                className={fieldClass(
                  'min-h-[64px] w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm outline-none ring-blue-500 focus:ring-2',
                  formErrors.objective,
                )}
                placeholder="Grow verified Accra listings by 40% and own the rental-price conversation."
                aria-invalid={!!formErrors.objective}
              />
              <FieldHint error={formErrors.objective} />
            </label>

            <div className="grid gap-3 sm:grid-cols-2">
              <label className="block space-y-1.5">
                <span className="text-xs font-medium text-gray-600">
                  Pillar <RequiredMark />
                </span>
                <select
                  value={form.pillar}
                  onChange={(e) => patchForm('pillar', e.target.value as PlayPillar)}
                  className={fieldClass(`${inputClass} w-full`, formErrors.pillar)}
                  aria-invalid={!!formErrors.pillar}
                >
                  {(Object.keys(PLAY_PILLAR_LABEL) as PlayPillar[]).map((p) => (
                    <option key={p} value={p}>
                      {PLAY_PILLAR_LABEL[p]}
                    </option>
                  ))}
                </select>
                <FieldHint error={formErrors.pillar} />
              </label>
              <label className="block space-y-1.5">
                <span className="text-xs font-medium text-gray-600">
                  Status <RequiredMark />
                </span>
                <select
                  value={form.status}
                  onChange={(e) => patchForm('status', e.target.value as PlayStatus)}
                  className={fieldClass(`${inputClass} w-full`, formErrors.status)}
                  aria-invalid={!!formErrors.status}
                >
                  {(Object.keys(PLAY_STATUS_LABEL) as PlayStatus[]).map((s) => (
                    <option key={s} value={s}>
                      {PLAY_STATUS_LABEL[s]}
                    </option>
                  ))}
                </select>
                <FieldHint error={formErrors.status} />
              </label>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <label className="block space-y-1.5">
                <span className="text-xs font-medium text-gray-600">
                  Start <RequiredMark />
                </span>
                <input
                  type="date"
                  value={form.start_date}
                  onChange={(e) => patchForm('start_date', e.target.value)}
                  className={fieldClass(`${inputClass} w-full`, formErrors.start_date)}
                  aria-invalid={!!formErrors.start_date}
                />
                <FieldHint error={formErrors.start_date} />
              </label>
              <label className="block space-y-1.5">
                <span className="text-xs font-medium text-gray-600">
                  End <RequiredMark />
                </span>
                <input
                  type="date"
                  value={form.end_date}
                  onChange={(e) => patchForm('end_date', e.target.value)}
                  className={fieldClass(`${inputClass} w-full`, formErrors.end_date)}
                  aria-invalid={!!formErrors.end_date}
                />
                <FieldHint error={formErrors.end_date} />
              </label>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <label className="block space-y-1.5">
                <span className="text-xs font-medium text-gray-600">Budget</span>
                <input
                  value={form.budget}
                  onChange={(e) => patchForm('budget', e.target.value)}
                  className={fieldClass(`${inputClass} w-full`, formErrors.budget)}
                  placeholder="25000"
                  inputMode="decimal"
                  aria-invalid={!!formErrors.budget}
                />
                <FieldHint error={formErrors.budget} />
              </label>
              <label className="block space-y-1.5">
                <span className="text-xs font-medium text-gray-600">Currency</span>
                <select
                  value={form.currency}
                  onChange={(e) => patchForm('currency', e.target.value as PlayCurrency)}
                  className={fieldClass(`${inputClass} w-full`, formErrors.currency)}
                  aria-invalid={!!formErrors.currency}
                >
                  {PLAY_CURRENCIES.map((code) => (
                    <option key={code} value={code}>
                      {PLAY_CURRENCY_LABEL[code]}
                    </option>
                  ))}
                </select>
                <FieldHint error={formErrors.currency} />
              </label>
            </div>

            <div className="space-y-3">
              <div className="space-y-1.5">
                <span className="text-xs font-medium text-gray-600">Verticals</span>
                <ChipMultiSelect
                  aria-label="Marketplace verticals"
                  options={VERTICAL_CHIP_OPTIONS}
                  value={form.verticals}
                  onChange={(verticals) => patchForm('verticals', verticals)}
                  emptyHint="Optional — pick marketplace surfaces this campaign covers"
                  disabled={createPlay.isPending}
                />
                <FieldHint error={formErrors.verticals} />
              </div>
              <div className="space-y-1.5">
                <span className="text-xs font-medium text-gray-600">Tags</span>
                <ChipMultiSelect
                  aria-label="Campaign tags"
                  options={TAG_CHIP_OPTIONS}
                  value={form.tags}
                  onChange={(tags) => patchForm('tags', tags)}
                  emptyHint="Optional — how this campaign is classified"
                  disabled={createPlay.isPending}
                />
                <FieldHint error={formErrors.tags} />
              </div>
            </div>

            <DialogFooter className="gap-2 sm:gap-0">
              <Button
                type="button"
                variant="outline"
                onClick={closeCreateDialog}
                disabled={createPlay.isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={createPlay.isPending}>
                {createPlay.isPending ? 'Creating…' : 'Create campaign'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog
        open={goalDialogOpen}
        onOpenChange={(open) => {
          if (open) setGoalDialogOpen(true);
          else closeCreateGoalDialog();
        }}
      >
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>New goal</DialogTitle>
            <DialogDescription>
              A tracked metric with baseline, target and current value. Link it to one or more
              campaigns so “did it work?” has an answer.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={submitGoal} className="space-y-4" noValidate>
            <TooltipProvider delayDuration={200}>
            <label className="block space-y-1.5">
              <FieldLabel
                required
                tip="The KPI name — what you are measuring (e.g. verified Accra listings, backlinks, lead→txn conversion)."
              >
                Metric
              </FieldLabel>
              <input
                value={goalForm.metric}
                onChange={(e) => patchGoalForm('metric', e.target.value)}
                className={fieldClass(`${inputClass} w-full`, goalFormErrors.metric)}
                placeholder="Verified Accra listings"
                aria-invalid={!!goalFormErrors.metric}
                autoFocus
              />
              <FieldHint error={goalFormErrors.metric} />
            </label>

            <div className="grid gap-3 sm:grid-cols-2">
              <label className="block space-y-1.5">
                <FieldLabel
                  required
                  tip="How often this number is reviewed. Pacing compares progress against time elapsed in the period."
                >
                  Cadence
                </FieldLabel>
                <select
                  value={goalForm.cadence}
                  onChange={(e) => patchGoalForm('cadence', e.target.value as GoalCadence)}
                  className={fieldClass(`${inputClass} w-full`, goalFormErrors.cadence)}
                  aria-invalid={!!goalFormErrors.cadence}
                >
                  {GOAL_CADENCES.map((c) => (
                    <option key={c} value={c}>
                      {GOAL_CADENCE_LABEL[c]}
                    </option>
                  ))}
                </select>
                <FieldHint error={goalFormErrors.cadence} />
              </label>
              <label className="block space-y-1.5">
                <FieldLabel
                  required
                  tip="How the value is expressed: a raw count, a percentage, or a money amount."
                >
                  Unit
                </FieldLabel>
                <select
                  value={goalForm.unit}
                  onChange={(e) => patchGoalForm('unit', e.target.value as GoalUnit)}
                  className={fieldClass(`${inputClass} w-full`, goalFormErrors.unit)}
                  aria-invalid={!!goalFormErrors.unit}
                >
                  {GOAL_UNITS.map((u) => (
                    <option key={u} value={u}>
                      {GOAL_UNIT_LABEL[u]}
                    </option>
                  ))}
                </select>
                <FieldHint error={goalFormErrors.unit} />
              </label>
            </div>

            <label className="block space-y-1.5">
              <FieldLabel tip="Where the authoritative number comes from — Ahrefs, the sales pipeline, the companion workbook, etc. Avoids two teams reporting different figures. Enter whatsapp:sent or whatsapp:replies to have the current value counted from the WhatsApp broadcasts tagged with this goal's campaigns.">
                Source of truth
              </FieldLabel>
              <input
                value={goalForm.source_of_truth}
                onChange={(e) => patchGoalForm('source_of_truth', e.target.value)}
                className={fieldClass(`${inputClass} w-full`, goalFormErrors.source_of_truth)}
                placeholder="Ahrefs / pipeline / workbook / whatsapp:replies"
                aria-invalid={!!goalFormErrors.source_of_truth}
              />
              <FieldHint error={goalFormErrors.source_of_truth} />
            </label>

            <div className="grid gap-3 sm:grid-cols-3">
              <label className="block space-y-1.5">
                <FieldLabel
                  required
                  tip="Where you started this period. Progress is measured from baseline to target — without it, the target alone is hard to judge."
                >
                  Baseline
                </FieldLabel>
                <input
                  value={goalForm.baseline}
                  onChange={(e) => patchGoalForm('baseline', e.target.value)}
                  className={fieldClass(`${inputClass} w-full`, goalFormErrors.baseline)}
                  inputMode="decimal"
                  aria-invalid={!!goalFormErrors.baseline}
                />
                <FieldHint error={goalFormErrors.baseline} />
              </label>
              <label className="block space-y-1.5">
                <FieldLabel
                  required
                  tip="The number you intend to reach by the end of the period."
                >
                  Target
                </FieldLabel>
                <input
                  value={goalForm.target}
                  onChange={(e) => patchGoalForm('target', e.target.value)}
                  className={fieldClass(`${inputClass} w-full`, goalFormErrors.target)}
                  inputMode="decimal"
                  aria-invalid={!!goalFormErrors.target}
                />
                <FieldHint error={goalFormErrors.target} />
              </label>
              <label className="block space-y-1.5">
                <FieldLabel
                  required
                  tip="The latest measured value. Update this as results come in; health compares it to target vs time elapsed."
                >
                  Current
                </FieldLabel>
                <input
                  value={goalForm.current_value}
                  onChange={(e) => patchGoalForm('current_value', e.target.value)}
                  className={fieldClass(`${inputClass} w-full`, goalFormErrors.current_value)}
                  inputMode="decimal"
                  aria-invalid={!!goalFormErrors.current_value}
                />
                <FieldHint error={goalFormErrors.current_value} />
              </label>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <label className="block space-y-1.5">
                <FieldLabel
                  required
                  tip="First day of the scoring window. Used with period end to compute pacing."
                >
                  Period start
                </FieldLabel>
                <input
                  type="date"
                  value={goalForm.period_start}
                  onChange={(e) => patchGoalForm('period_start', e.target.value)}
                  className={fieldClass(`${inputClass} w-full`, goalFormErrors.period_start)}
                  aria-invalid={!!goalFormErrors.period_start}
                />
                <FieldHint error={goalFormErrors.period_start} />
              </label>
              <label className="block space-y-1.5">
                <FieldLabel
                  required
                  tip="Last day of the scoring window. Must be on or after the start date."
                >
                  Period end
                </FieldLabel>
                <input
                  type="date"
                  value={goalForm.period_end}
                  onChange={(e) => patchGoalForm('period_end', e.target.value)}
                  className={fieldClass(`${inputClass} w-full`, goalFormErrors.period_end)}
                  aria-invalid={!!goalFormErrors.period_end}
                />
                <FieldHint error={goalFormErrors.period_end} />
              </label>
            </div>

            <div className="space-y-1.5">
              <FieldLabel tip="Campaigns this KPI hangs off. One goal can span several campaigns; leave empty to link later.">
                Campaigns
              </FieldLabel>
              <ChipMultiSelect
                aria-label="Link campaigns"
                options={plays.map((p) => ({
                  value: String(p.play_id),
                  label: p.name,
                }))}
                value={goalForm.play_ids.map(String)}
                onChange={(ids) =>
                  patchGoalForm(
                    'play_ids',
                    ids.map(Number).filter((n) => Number.isFinite(n)),
                  )
                }
                emptyHint="Optional — leave unlinked and attach later"
                disabled={createGoal.isPending || plays.length === 0}
              />
              <FieldHint error={goalFormErrors.play_ids} />
            </div>
            </TooltipProvider>

            <DialogFooter className="gap-2 sm:gap-0">
              <Button
                type="button"
                variant="outline"
                onClick={closeCreateGoalDialog}
                disabled={createGoal.isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={createGoal.isPending}>
                {createGoal.isPending ? 'Creating…' : 'Create goal'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {linksFor && (
        <PlayLinksDialog
          playId={linksFor.play_id}
          playName={linksFor.name}
          open
          onOpenChange={(v) => !v && setLinksFor(null)}
        />
      )}

      <Dialog open={!!deleting} onOpenChange={(v) => !v && setDeleting(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Remove campaign?</DialogTitle>
            <DialogDescription>
              {deleting && (
                <>
                  <strong>{deleting.name}</strong> will be removed. Its{' '}
                  {deleting.rollup.content + deleting.rollup.social + deleting.rollup.traditional}{' '}
                  attached item(s) and {deleting.rollup.goals} goal(s) survive, but stop being
                  attributed to any campaign.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={() => setDeleting(null)}>
              Cancel
            </Button>
            <Button
              type="button"
              className="bg-red-600 hover:bg-red-700"
              onClick={confirmDelete}
            >
              Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </MarketingPageShell>
  );
}
